import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../contexts/AuthContext';
import Logger from '../utils/logger';
import { OidcProvider, parseOidcReturnFragment } from '../utils/googleReturn';

/**
 * Page d'atterrissage du retour d'un fournisseur OIDC : /login/google#status=...
 * ou /login/entra#status=... Le fournisseur se lit dans le chemin.
 *
 * Hors PublicRoute : un utilisateur deja connecte y revient apres avoir lie
 * une identite depuis son profil (status=linked). Le fragment est lu une fois
 * puis efface de l'URL avant toute navigation.
 */
const OidcReturnPage: React.FC = () => {
  // @ts-ignore -- TS2589: useTranslation type instantiation too deep
  const { t } = (useTranslation as any)('auth');
  const { handleOidcReturn } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const done = useRef(false);
  const provider: OidcProvider = pathname.startsWith('/login/entra') ? 'entra' : 'google';

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const ret = parseOidcReturnFragment(window.location.hash, provider);
    window.history.replaceState(null, '', window.location.pathname);

    const run = async () => {
      try {
        await handleOidcReturn(ret, provider);
      } catch (err) {
        Logger.error('OIDC return handling failed', err);
        navigate('/login', { replace: true, state: { oidcError: `${provider}_unavailable` } });
        return;
      }
      switch (ret.status) {
        case 'ok':
          navigate(ret.next || '/dashboard', { replace: true });
          break;
        case 'mfa':
          navigate('/login/mfa', { replace: true });
          break;
        case 'device':
          navigate('/login/verify-device', { replace: true });
          break;
        case 'link':
          navigate(`/login/${provider}/link`, { replace: true });
          break;
        case 'linked':
          navigate(ret.next || '/profile', { replace: true, state: { oidcLinked: provider } });
          break;
        default:
          navigate('/login', { replace: true, state: { oidcError: ret.code || `${provider}_unavailable` } });
      }
    };
    run();
  }, [handleOidcReturn, navigate, provider]);

  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-400 mx-auto" />
        <p className="mt-4 text-sm text-text-secondary">
          {provider === 'entra' ? t('oidcReturn.loadingEntra') : t('googleReturn.loading')}
        </p>
      </div>
    </div>
  );
};

export default OidcReturnPage;
